import ky from 'ky';
import loki from 'lokijs';
import Config from '../../config';

export interface PubmedPdfAuthorData {
    id: number;
    name: string;
    count: number;
}

export interface PubmedPdfJournalData {
    id: number;
    name: string;
    count: number;
}

export interface PubmedPdfPaperData {
    id: number;
    pmid: number;
    journal_id: number;
    year: number;
    volume: string;
    page: string;
    author_ids: number[];
    title: string;
}

export interface PubmedPdfYearData {
    year: number;
    count: number;
}

export interface PubmedPdfIndexData {
    name: string;
    author: PubmedPdfAuthorData[];
    journal: PubmedPdfJournalData[];
    paper: PubmedPdfPaperData[];
}

export interface PubmedPdfBreadcrumbsData {
    id: string;
    name: string;
    link: string;
}

class PubmedPdfUtils {
    private pathname: string;
    private database: loki;
    private name: string;
    private authors: Collection<PubmedPdfAuthorData>;
    private journals: Collection<PubmedPdfJournalData>;
    private papers: Collection<PubmedPdfPaperData>;
    private years: Collection<PubmedPdfYearData>;
    private authorInitials: string[];
    private journalInitials: string[];
    private initializing: Promise<boolean> | null;

    constructor(path: string) {
        this.pathname = path;
        this.database = new loki(path);
        this.name = '';
        this.authors = this.database.addCollection('authors');
        this.journals = this.database.addCollection('journals');
        this.papers = this.database.addCollection('papers');
        this.years = this.database.addCollection('years');
        this.authorInitials = [];
        this.journalInitials = [];
        this.initializing = null;
    }

    getIndexUrl(): string {
        return `${this.pathname}/index.php`;
    }

    getAuthorUrl(id: number, year: number): string {
        return `${this.pathname}/author.php?author=${id}${year !== 0 ? `&year=${year}` : ''}`;
    }

    getAuthorInitialUrl(initial: string): string {
        return `${this.pathname}/author.php?initial=${initial}`;
    }

    getYearUrl(year: number, journalId: number): string {
        return `${this.pathname}/year.php?year=${year}${journalId !== 0 ? `&journal=${journalId}` : ''}`;
    }

    getJournalUrl(journalId: number, year: number): string {
        return `${this.pathname}/journal.php?journal=${journalId}${year !== 0 ? `&year=${year}` : ''}`;
    }

    getPaperDescUrl(id: number): string {
        return `${this.pathname}/paper_desc.php?id=${id}`;
    }

    getName(): string {
        return this.name;
    }

    getPaper(id: number): PubmedPdfPaperData | null {
        return this.papers.findOne({ id: id });
    }

    getAuthor(authorId: number): PubmedPdfAuthorData | null {
        return this.authors.findOne({ id: authorId });
    }

    getJournal(journalId: number): PubmedPdfJournalData | null {
        return this.journals.findOne({ id: journalId });
    }

    getAuthorInitials(): string[] {
        return this.authorInitials;
    }

    getJournalInitials(): string[] {
        return this.journalInitials;
    }

    getYears(): PubmedPdfYearData[] {
        return this.years.chain().find().simplesort('year').data();
    }

    getYearsByPapers(papers: PubmedPdfPaperData[]) {
        const count: { [year: number]: number } = {};
        papers.forEach((paper) => {
            if (typeof count[paper.year] === 'undefined') {
                count[paper.year] = 0;
            }
            count[paper.year]++;
        });
        const years = Object.keys(count).map((key) => parseInt(key, 10));
        years.sort((a, b) => a - b);
        return years.map((year) => {
            return { year: year, count: count[year] };
        });
    }

    getAuthors(initial?: string): PubmedPdfAuthorData[] {
        const filter: LokiQuery<PubmedPdfAuthorData & LokiObj> = {};
        if (typeof initial !== 'undefined') {
            filter.name = { $regex: [`^${initial}`, 'i'] };
        }
        return this.authors.chain().find(filter).simplesort('name').data();
    }

    getJournalsByInitial(initial: string): PubmedPdfJournalData[] {
        const filter = { name: { $regex: [`^${initial}`, 'i'] } };
        return this.journals.chain().find(filter).simplesort('name').data();
    }

    getJournalsByYear(year: number): PubmedPdfJournalData[] {
        const filter = { year: year };
        const count: { [id: number]: number } = {};
        const papers = this.papers.find(filter);
        papers.forEach((paper) => {
            if (typeof count[paper.journal_id] === 'undefined') {
                count[paper.journal_id] = 0;
            }
            count[paper.journal_id]++;
        });
        return Object.keys(count).map((journalIdStr) => {
            const journalId = parseInt(journalIdStr, 10);
            const journal = this.getJournal(journalId);
            return {
                id: journalId,
                name: journal !== null ? journal.name : '',
                count: count[journalId],
            };
        });
    }

    getPapersByAuthorId(authorId: number, year: number): PubmedPdfPaperData[] {
        const filter: LokiQuery<PubmedPdfPaperData & LokiObj> = { author_ids: { $contains: authorId } };
        if (year !== 0) {
            filter.year = year;
        }
        return this.papers.chain().find(filter).simplesort('year').data();
    }

    getPapersByJournalId(journalId: number, year: number): PubmedPdfPaperData[] {
        const filter: LokiQuery<PubmedPdfPaperData & LokiObj> = { journal_id: journalId };
        if (year !== 0) {
            filter.year = year;
        }
        return this.papers.chain().find(filter).simplesort('year').data();
    }

    getCountPapers(): number {
        return this.papers.count();
    }

    /**
     * Loads the module index once. Concurrent callers share the same request,
     * so the collections are never filled twice.
     */
    initialize = (): Promise<boolean> => {
        if (this.initializing === null) {
            this.initializing = ky
                .get(new URL(`${Config.BASE_URL}${this.pathname}/index.json`, window.location.href))
                .json<PubmedPdfIndexData>()
                .then((index) => {
                    this.name = index.name;
                    index.author.forEach((value) => {
                        const initial = this.getInitial(value.name);
                        if (!this.authorInitials.includes(initial)) {
                            this.authorInitials.push(initial);
                        }
                        this.authors.insert(value);
                    });
                    this.authorInitials.sort();
                    index.journal.forEach((value) => {
                        const initial = this.getInitial(value.name);
                        if (!this.journalInitials.includes(initial)) {
                            this.journalInitials.push(initial);
                        }
                        this.journals.insert(value);
                    });
                    this.journalInitials.sort();
                    index.paper.forEach((value) => {
                        const year = this.years.findOne({ year: value.year });
                        if (year !== null) {
                            year.count++;
                            this.years.update(year);
                        } else {
                            this.years.insert({ year: value.year, count: 1 });
                        }
                        this.papers.insert(value);
                    });
                    return true;
                })
                .catch(() => false);
        }
        return this.initializing;
    };

    getInitial(name: string) {
        return name.charAt(0).toUpperCase();
    }
}

export default new PubmedPdfUtils('/modules/PubMedPDF');
